import React, { useState } from 'react';
import { X, User, Star, Send } from 'lucide-react';
import { backendUrl } from '../utils/socketClient';

interface CustomReplyModalProps {
  storeName: string;
  onClose: () => void;
}

const CustomReplyModal: React.FC<CustomReplyModalProps> = ({ storeName, onClose }) => {
  const [reviewerName, setReviewerName] = useState('');
  const [starRating, setStarRating] = useState('5');
  const [reviewContent, setReviewContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const generateReply = async () => {
    if (!reviewContent.trim()) {
      alert("口コミ例を入力してください");
      return;
    }

    setIsGenerating(true);
    setReplyContent("返信生成中...");

    try {
      const response = await fetch(`${backendUrl}/generate_reply/${encodeURIComponent(storeName)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          review_content: reviewContent,
          star_rating: starRating,
          reviewer_name: reviewerName || "〇〇〇"
        })
      });

      if (!response.ok) {
        throw new Error("HTTP error " + response.status);
      }

      const data = await response.json();
      if (data.reply) {
        setReplyContent(data.reply);
      } else if (data.error) {
        setReplyContent("返信生成エラー: " + data.error);
      }
    } catch (error) {
      console.error("返信生成エラー:", error);
      setReplyContent("返信生成中にエラーが発生しました");
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStarInput = () => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((value) => (
          <Star
            key={value}
            className={`h-6 w-6 cursor-pointer ${
              parseInt(starRating) >= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
            onClick={() => setStarRating(value.toString())}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Modal Header */}
          <div className="bg-green-600 px-4 py-3 sm:px-6 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-white" id="modal-title">
              返信自由生成
            </h3>
            <button
              type="button"
              className="text-white hover:text-gray-200 focus:outline-none"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Review Info Inputs */}
            <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="reviewerName" className="block text-sm font-medium text-gray-700 mb-1">
                  投稿者名
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="reviewerName"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    className="focus:ring-[#007AFF] focus:border-[#007AFF] block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                    placeholder="投稿者名を入力"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  星評価
                </label>
                {renderStarInput()}
              </div>
            </div>

            {/* Review Content and Reply */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  口コミ内容
                </label>
                <textarea
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                  className="shadow-sm focus:ring-[#007AFF] focus:border-[#007AFF] block w-full sm:text-sm border-gray-300 rounded-md h-64 p-3"
                  placeholder="ここに返信用の口コミ例を入力"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  返信内容
                </label>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="shadow-sm focus:ring-[#007AFF] focus:border-[#007AFF] block w-full sm:text-sm border-gray-300 rounded-md h-64 p-3"
                  placeholder="ここに生成された返信が表示されます"
                  readOnly={isGenerating}
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={generateReply}
              disabled={isGenerating}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  生成中...
                </span>
              ) : (
                <span className="flex items-center">
                  <Send className="mr-2 h-4 w-4" />
                  返信生成
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReplyModal;
